/* eslint-disable no-restricted-globals */
import React, { useCallback, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  EmployeeOfferDetails,
  Financier,
  MonetaryDetailsV2,
  PaymentDetails,
} from "../../../../types/Transaction";
import {
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../../../components/utils";
import { Input, Label } from "../../../../components/shared/InputField";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../../../components/shared/RadioGroup";
import { Select } from "../../../../components/shared/Select";
import { addExternalPaymentDetails } from "../../../../api/TransactionApisV2";
import { isPaymentAmountComplete } from "../../../../utils/paymentCompletion";

const PAYMENT_MODES = [
  { value: "UPI", label: "UPI" },
  { value: "NetBanking", label: "NetBanking" },
  { value: "RTGS", label: "RTGS" },
  { value: "NEFT", label: "NEFT" },
  { value: "Card-Based Payments", label: "Card-Based Payments" },
  { value: "Others", label: "Others" },
];

type AddExternalPaymentDetailsProps = {
  monetaryDetails: MonetaryDetailsV2;
  onClose: () => void;
  onSuccess?: () => void;
  transactionId?: string;
  employee?: EmployeeOfferDetails;
  paymentDetails?: PaymentDetails[];
};

const paymentStatusConfig: Record<
  string,
  { label: string; className: string }
> = {
  PAYMENT_PENDING: {
    label: "Pending Approval",
    className: "bg-yellow-100 text-yellow-800 border border-yellow-200",
  },
  SUCCESS: {
    label: "Approved",
    className: "bg-green-100 text-green-800 border border-green-200",
  },
  FAILED: {
    label: "Rejected",
    className: "bg-red-100 text-red-800 border border-red-200",
  },
};

const StatusBadge = ({ status }: { status?: string }) => {
  if (!status) return null;
  const config = paymentStatusConfig[status] || {
    label: status,
    className: "bg-gray-100 text-gray-700",
  };
  return (
    <span
      className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}
    >
      {config.label}
    </span>
  );
};

const formatDateDisplay = (dateStr: string | Date | undefined): string => {
  if (!dateStr) return "—";
  const d = new Date(dateStr as string);
  if (isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

type PaymentType = "Exercise Price" | "Tax Price";
type FinancingType = "SELF" | "ESOP_FINANCING";

export interface TrancheFormValues {
  paymentDate: string;
  referenceId: string;
  amount: string;
  modeOfPayment: string;
  customModeOfPayment: string;
  files: File[];
  financingType: FinancingType;
  financierId: string;
}

type ExternalPaymentFormDataInput = {
  employeeId: string;
  transactionId: string;
  requestId?: string;
  paymentType: PaymentType;
  values: TrancheFormValues;
  financiers: Financier[];
};

const formatToday = () => new Date().toISOString().slice(0, 10);

export function buildExternalPaymentFormData({
  employeeId,
  transactionId,
  requestId,
  paymentType,
  values,
  financiers,
}: ExternalPaymentFormDataInput): FormData {
  const formData = new FormData();
  const isEsopFinancing = values.financingType === "ESOP_FINANCING";
  const financier = financiers.find((item) => item.id === values.financierId);

  formData.append("employeeId", employeeId);
  formData.append("transactionId", transactionId);
  if (requestId) formData.append("requestId", requestId);
  formData.append(
    "paymentDetails[0][paymentDate]",
    isEsopFinancing ? formatToday() : values.paymentDate
  );
  formData.append("paymentDetails[0][paymentType]", paymentType);
  formData.append(
    "paymentDetails[0][referenceId]",
    isEsopFinancing ? financier?.name || "ESOP Financing" : values.referenceId
  );
  formData.append("paymentDetails[0][amount]", values.amount);
  formData.append("paymentDetails[0][currency]", "INR");

  if (isEsopFinancing) {
    formData.append(
      "paymentDetails[0][metaData][financingType]",
      "ESOP_FINANCING"
    );
    formData.append(
      "paymentDetails[0][metaData][financierId]",
      values.financierId
    );
  } else {
    formData.append(
      "paymentDetails[0][metaData][modeOfPayment]",
      values.modeOfPayment === "Others"
        ? values.customModeOfPayment.trim()
        : values.modeOfPayment
    );
    values.files.forEach((file, index) => {
      formData.append(`paymentDetails[0][files][${index}]`, file);
    });
  }

  return formData;
}

const NewTrancheForm: React.FC<{
  totalRequired: number;
  totalApproved: number;
  financingEnabled: boolean;
  financiers: Financier[];
  lockedFinancingType: FinancingType | null;
  onSubmit: (values: TrancheFormValues) => Promise<void>;
  isSubmitting: boolean;
}> = ({
  totalRequired,
  totalApproved,
  financingEnabled,
  financiers,
  lockedFinancingType,
  onSubmit,
  isSubmitting,
}) => {
  const remaining = Math.max(0, totalRequired - totalApproved);
  const formik = useFormik<TrancheFormValues>({
    initialValues: {
      paymentDate: "",
      referenceId: "",
      amount: remaining > 0 ? String(remaining) : "",
      modeOfPayment: "",
      customModeOfPayment: "",
      files: [],
      financingType: lockedFinancingType || "SELF",
      financierId: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      paymentDate: Yup.string().when("financingType", {
        is: "SELF",
        then: (schema) => schema.required("Payment date is required"),
      }),
      referenceId: Yup.string().when("financingType", {
        is: "SELF",
        then: (schema) => schema.required("Reference ID is required"),
      }),
      amount: Yup.number()
        .typeError("Must be a number")
        .positive("Must be positive")
        .required("Amount is required"),
      modeOfPayment: Yup.string().when("financingType", {
        is: "SELF",
        then: (schema) => schema.required("Mode of payment is required"),
      }),
      customModeOfPayment: Yup.string().test(
        "requires-custom-payment-mode",
        "Other mode of payment is required",
        function requiresCustomPaymentMode(value) {
          const values = this.parent as TrancheFormValues;
          return (
            values.financingType !== "SELF" ||
            values.modeOfPayment !== "Others" ||
            Boolean(value?.trim())
          );
        }
      ),
      financierId: Yup.string().when("financingType", {
        is: "ESOP_FINANCING",
        then: (schema) => schema.required("Choose a financier"),
      }),
    }),
    onSubmit: async (values, helpers) => {
      await onSubmit(values);
      helpers.resetForm();
    },
  });

  const isEsopFinancing = formik.values.financingType === "ESOP_FINANCING";
  const selectedFinancier = financiers.find(
    (financier) => financier.id === formik.values.financierId
  );

  const selectFinancingType = (financingType: FinancingType) => {
    formik.setFieldValue("financingType", financingType);
    if (financingType === "ESOP_FINANCING") {
      formik.setFieldValue("amount", String(totalRequired));
      formik.setFieldValue("referenceId", "");
      formik.setFieldValue("paymentDate", "");
      formik.setFieldValue("modeOfPayment", "");
      formik.setFieldValue("customModeOfPayment", "");
      formik.setFieldValue("files", []);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <VStack className="gap-4 rounded-lg border border-borderColor bg-slate-light p-4">
        <p className="text-base font-semibold text-headerColor">Add payment</p>
        {remaining > 0 && (
          <p className="text-sm text-secondaryText">
            Remaining:{" "}
            <span className="font-semibold text-headerColor">
              ₹{remaining.toLocaleString("en-IN")}
            </span>
          </p>
        )}
        {financingEnabled && !lockedFinancingType && (
          <VStack className="gap-2">
            <Label className="mb-0">Payment method</Label>
            <RadioGroup
              name="financingType"
              value={formik.values.financingType}
              onValueChange={(value) =>
                selectFinancingType(value as FinancingType)
              }
              disabled={isSubmitting}
              className="grid-cols-1 gap-2 sm:grid-cols-2"
            >
              <Label
                htmlFor="self-financed-payment"
                className="mb-0 flex cursor-pointer items-center gap-2 rounded-lg border border-inputBorder bg-white p-3 text-sm text-primaryText"
              >
                <RadioGroupItem id="self-financed-payment" value="SELF" />
                Self financed
              </Label>
              <Label
                htmlFor="esop-financed-payment"
                className="mb-0 flex cursor-pointer items-center gap-2 rounded-lg border border-inputBorder bg-white p-3 text-sm text-primaryText"
              >
                <RadioGroupItem
                  id="esop-financed-payment"
                  value="ESOP_FINANCING"
                />
                ESOP Financing
              </Label>
            </RadioGroup>
          </VStack>
        )}
        {lockedFinancingType && (
          <p className="text-xs text-secondaryText">
            This payment is locked to{" "}
            {lockedFinancingType === "ESOP_FINANCING"
              ? "ESOP Financing"
              : "self financing"}
            .
          </p>
        )}
        {isEsopFinancing ? (
          <VStack className="gap-2">
            <Label htmlFor="financierId" className="mb-0 required">
              Financier
            </Label>
            <Select
              id="financierId"
              name="financierId"
              options={financiers}
              valueGetter={(financier) => financier.id}
              textGetter={(financier) => financier.name}
              value={formik.values.financierId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isSubmitting || financiers.length === 0}
              placeholder="Choose a financier"
              className="w-full"
            />
            {financiers.length === 0 ? (
              <p className="text-xs text-red-500">
                No financiers are configured for this payment.
              </p>
            ) : selectedFinancier?.details ? (
              <p className="text-xs text-secondaryText">
                {selectedFinancier.details}
              </p>
            ) : null}
            {formik.touched.financierId && formik.errors.financierId && (
              <p className="text-xs text-red-500">
                {formik.errors.financierId}
              </p>
            )}
            <p className="text-xs text-secondaryText">
              The full remaining amount will be submitted through the selected
              financier.
            </p>
          </VStack>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <VStack className="w-full gap-2">
              <Label htmlFor="amount" className="mb-0 required">
                Amount
              </Label>
              <Input
                id="amount"
                type="number"
                name="amount"
                placeholder="e.g. 5000"
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isSubmitting}
                className="w-full"
              />
              {formik.touched.amount && formik.errors.amount && (
                <p className="text-xs text-red-500">{formik.errors.amount}</p>
              )}
            </VStack>
            <VStack className="w-full gap-2">
              <Label htmlFor="paymentDate" className="mb-0 required">
                Payment date
              </Label>
              <Input
                id="paymentDate"
                type="date"
                name="paymentDate"
                value={formik.values.paymentDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isSubmitting}
                className="w-full"
              />
              {formik.touched.paymentDate && formik.errors.paymentDate && (
                <p className="text-xs text-red-500">
                  {formik.errors.paymentDate}
                </p>
              )}
            </VStack>
            <VStack className="w-full gap-2">
              <Label htmlFor="referenceId" className="mb-0 required">
                Reference ID
              </Label>
              <Input
                id="referenceId"
                type="text"
                name="referenceId"
                placeholder="Transaction ID"
                value={formik.values.referenceId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isSubmitting}
                className="w-full"
              />
              {formik.touched.referenceId && formik.errors.referenceId && (
                <p className="text-xs text-red-500">
                  {formik.errors.referenceId}
                </p>
              )}
            </VStack>
            <VStack className="w-full gap-2">
              <Label htmlFor="modeOfPayment" className="mb-0 required">
                Payment method
              </Label>
              <Select
                id="modeOfPayment"
                name="modeOfPayment"
                options={PAYMENT_MODES}
                valueGetter={(option) => option.value}
                textGetter={(option) => option.label}
                value={formik.values.modeOfPayment}
                onChange={(event) => {
                  formik.setFieldValue("modeOfPayment", event.target.value);
                  if (event.target.value !== "Others") {
                    formik.setFieldValue("customModeOfPayment", "");
                  }
                }}
                onBlur={formik.handleBlur}
                disabled={isSubmitting}
                className="w-full"
                placeholder="Select payment method"
              />
              {formik.touched.modeOfPayment && formik.errors.modeOfPayment && (
                <p className="text-xs text-red-500">
                  {formik.errors.modeOfPayment}
                </p>
              )}
              {formik.values.modeOfPayment === "Others" && (
                <VStack className="gap-2">
                  <Label
                    htmlFor="customModeOfPayment"
                    className="mb-0 required"
                  >
                    Other payment method
                  </Label>
                  <Input
                    id="customModeOfPayment"
                    type="text"
                    name="customModeOfPayment"
                    placeholder="Enter payment method"
                    value={formik.values.customModeOfPayment}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isSubmitting}
                    className="w-full"
                  />
                  {formik.touched.customModeOfPayment &&
                    formik.errors.customModeOfPayment && (
                      <p className="text-xs text-red-500">
                        {formik.errors.customModeOfPayment}
                      </p>
                    )}
                </VStack>
              )}
            </VStack>
            <VStack className="w-full gap-2">
              <Label htmlFor="files" className="mb-0">
                Receipts
              </Label>
              <Input
                id="files"
                type="file"
                accept="image/*,application/pdf"
                name="files"
                multiple
                className="w-full"
                onChange={(event) =>
                  formik.setFieldValue(
                    "files",
                    Array.from(event.currentTarget.files ?? [])
                  )
                }
                disabled={isSubmitting}
              />
              {formik.values.files.length > 0 && (
                <p className="text-xs text-secondaryText">
                  {formik.values.files.length} file(s) selected
                </p>
              )}
            </VStack>
          </div>
        )}
        <HStack className="justify-end">
          <ButtonPrimary
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
            loadingText="Saving..."
          >
            Save payment
          </ButtonPrimary>
        </HStack>
      </VStack>
    </form>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const AddExternalPaymentDetails = (props: AddExternalPaymentDetailsProps) => {
  const {
    monetaryDetails,
    employee,
    onClose,
    onSuccess,
    paymentDetails = [],
  } = props;

  const exercisePaymentLink = monetaryDetails.paymentLink;
  const taxPaymentLink = monetaryDetails.taxPaymentLink;

  const exerciseTranches = paymentDetails.filter(
    (p) => p.paymentType.toLowerCase() === "exercise price"
  );
  const taxTranches = paymentDetails.filter(
    (p) => p.paymentType.toLowerCase() === "tax price"
  );

  const exercisePaymentConfigured = exercisePaymentLink != null;
  const taxPaymentConfigured =
    !!monetaryDetails.taxPaymentEnabled && taxPaymentLink != null;
  const showExerciseSection =
    exercisePaymentConfigured ||
    monetaryDetails.exerciseEsopFinancingEnabled ||
    exerciseTranches.length > 0;
  const showTaxSection =
    taxPaymentConfigured ||
    monetaryDetails.taxEsopFinancingEnabled ||
    taxTranches.length > 0;

  const totalApprovedExercise = exerciseTranches
    .filter((p) => p.paymentStatus === "SUCCESS")
    .reduce((s, p) => s + (p.amount ?? 0), 0);
  const totalRequiredExercise =
    (employee as any)?.transactionCompensationDetails?.totalStrikePrice ?? 0;
  const isExerciseFullyPaid = isPaymentAmountComplete(
    totalRequiredExercise,
    totalApprovedExercise
  );

  const totalApprovedTax = taxTranches
    .filter((p) => p.paymentStatus === "SUCCESS")
    .reduce((s, p) => s + (p.amount ?? 0), 0);
  const totalRequiredTax =
    (employee as any)?.transactionCompensationDetails?.totalTaxAmount ?? 0;
  const isTaxFullyPaid = isPaymentAmountComplete(
    totalRequiredTax,
    totalApprovedTax
  );

  const [isSavingTranche, setIsSavingTranche] = useState(false);
  const isSubmitting = isSavingTranche;

  const saveTranche = async (
    paymentType: PaymentType,
    values: TrancheFormValues,
    financiers: Financier[]
  ) => {
    if (!employee?.employeeId || !props.transactionId) {
      toast.error("Details not found");
      return;
    }
    const formData = buildExternalPaymentFormData({
      employeeId: employee.employeeId,
      transactionId: props.transactionId,
      requestId: employee.requestId,
      paymentType,
      values,
      financiers,
    });
    setIsSavingTranche(true);
    try {
      await addExternalPaymentDetails(formData);
      onSuccess?.();
      toast.success(
        paymentType === "Exercise Price"
          ? "Payment details saved"
          : "Tax payment details saved"
      );
      onClose();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.errorRaw || "Failed to save payment details"
      );
    } finally {
      setIsSavingTranche(false);
    }
  };

  const handleDownload = useCallback((s3Url: string) => {
    if (s3Url) window.open(s3Url, "_blank");
    else toast.error("Failed to get download link");
  }, []);

  const financingTypeOf = (
    tranches: PaymentDetails[]
  ): FinancingType | null => {
    const active = tranches.find(
      (tranche) =>
        !["FAILED", "PAYMENT_FAILED", "REJECTED"].includes(
          tranche.paymentStatus
        )
    );
    if (!active) return null;
    return active.metaData?.financingType === "ESOP_FINANCING"
      ? "ESOP_FINANCING"
      : "SELF";
  };

  const exerciseFinancingType = financingTypeOf(exerciseTranches);
  const taxFinancingType = financingTypeOf(taxTranches);
  const canAddMoreTranches =
    (exercisePaymentConfigured ||
      monetaryDetails.exerciseEsopFinancingEnabled) &&
    !isExerciseFullyPaid &&
    exerciseFinancingType !== "ESOP_FINANCING";
  const canAddMoreTaxTranches =
    (taxPaymentConfigured || monetaryDetails.taxEsopFinancingEnabled) &&
    !isTaxFullyPaid &&
    taxFinancingType !== "ESOP_FINANCING";

  const TrancheTable = ({ tranches }: { tranches: PaymentDetails[] }) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
      <table className="w-full text-sm text-left">
        <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
          <tr>
            <th className="px-4 py-3 font-medium">#</th>
            <th className="px-4 py-3 font-medium">Reference ID</th>
            <th className="px-4 py-3 font-medium">Date</th>
            <th className="px-4 py-3 text-right font-medium">Amount</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Proof</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {tranches.map((tranche, idx) => (
            <tr key={tranche.id ?? idx} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
              <td className="px-4 py-3 font-medium text-gray-900">
                {tranche.referenceId || "—"}
              </td>
              <td className="px-4 py-3 text-gray-600">
                {formatDateDisplay(tranche.paymentDate)}
              </td>
              <td className="px-4 py-3 text-right font-semibold text-gray-900">
                ₹{(tranche.amount ?? 0).toLocaleString("en-IN")}
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={tranche.paymentStatus} />
              </td>
              <td className="px-4 py-3">
                {tranche.documentLinks && tranche.documentLinks.length > 0 ? (
                  <div className="flex flex-col gap-1.5">
                    {tranche.documentLinks.map((doc, di) =>
                      doc.s3Url ? (
                        <button
                          key={doc.id ?? di}
                          type="button"
                          className="text-primary hover:text-primaryHover hover:underline text-left truncate max-w-[150px] transition-colors focus:outline-none focus:ring-1 focus:ring-primary rounded px-1 -ml-1"
                          onClick={() => handleDownload(doc.s3Url!)}
                          title={doc.name || "View"}
                        >
                          {doc.name || `File ${di + 1}`}
                        </button>
                      ) : null
                    )}
                  </div>
                ) : (
                  <span className="text-gray-400 italic">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="max-w-4xl w-full mx-auto bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200">
        <div>
          <h6 className="text-lg font-semibold text-gray-900">
            Add Payment Details
          </h6>
          <p className="mt-1 text-sm text-gray-500">
            {employee?.employeeName || "Employee"}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/20 rounded p-1"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <VStack className="gap-6 p-6">
        {!showExerciseSection && !showTaxSection && (
          <div className="py-12 text-sm text-center text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
            No payment details configured for this transaction.
          </div>
        )}

        {/* ── Exercise Payment Section ───────────────────────── */}
        {showExerciseSection && (
          <VStack className="gap-4">
            <HStack className="items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">
                Exercise Payment
              </span>
              {totalRequiredExercise > 0 && (
                <span className="text-sm text-gray-500">
                  Approved:{" "}
                  <span
                    className={`font-semibold ${
                      isExerciseFullyPaid ? "text-green-600" : "text-primary"
                    }`}
                  >
                    ₹{totalApprovedExercise.toLocaleString("en-IN")}
                  </span>
                  {" / "}₹{totalRequiredExercise.toLocaleString("en-IN")}
                </span>
              )}
            </HStack>

            {/* Progress bar */}
            {totalRequiredExercise > 0 && exerciseTranches.length > 0 && (
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${
                    isExerciseFullyPaid ? "bg-green-600" : "bg-primary"
                  }`}
                  style={{
                    width: `${Math.min(
                      100,
                      (totalApprovedExercise / totalRequiredExercise) * 100
                    )}%`,
                  }}
                />
              </div>
            )}

            {exerciseTranches.length > 0 && (
              <TrancheTable tranches={exerciseTranches} />
            )}

            {canAddMoreTranches && (
              <NewTrancheForm
                totalRequired={totalRequiredExercise}
                totalApproved={totalApprovedExercise}
                financingEnabled={
                  monetaryDetails.exerciseEsopFinancingEnabled || false
                }
                financiers={monetaryDetails.exerciseFinanciers || []}
                lockedFinancingType={exerciseFinancingType}
                onSubmit={(values) =>
                  saveTranche(
                    "Exercise Price",
                    values,
                    monetaryDetails.exerciseFinanciers || []
                  )
                }
                isSubmitting={isSavingTranche}
              />
            )}

            {exerciseTranches.length === 0 && !canAddMoreTranches && (
              <div className="text-sm text-gray-400 py-2">
                No payment details yet.
              </div>
            )}
          </VStack>
        )}

        {/* ── Tax Payment Section ───────────────────────────── */}
        {showTaxSection && (
          <VStack className="gap-4">
            <HStack className="items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">
                Tax Payment
              </span>
              {totalRequiredTax > 0 && (
                <span className="text-sm text-gray-500">
                  Approved:{" "}
                  <span
                    className={`font-semibold ${
                      isTaxFullyPaid ? "text-green-600" : "text-primary"
                    }`}
                  >
                    ₹{totalApprovedTax.toLocaleString("en-IN")}
                  </span>
                  {" / "}₹{totalRequiredTax.toLocaleString("en-IN")}
                </span>
              )}
            </HStack>

            {/* Progress bar */}
            {totalRequiredTax > 0 && taxTranches.length > 0 && (
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${
                    isTaxFullyPaid ? "bg-green-600" : "bg-primary"
                  }`}
                  style={{
                    width: `${Math.min(
                      100,
                      (totalApprovedTax / totalRequiredTax) * 100
                    )}%`,
                  }}
                />
              </div>
            )}

            {taxTranches.length > 0 && <TrancheTable tranches={taxTranches} />}

            {canAddMoreTaxTranches && (
              <NewTrancheForm
                totalRequired={totalRequiredTax}
                totalApproved={totalApprovedTax}
                financingEnabled={
                  monetaryDetails.taxEsopFinancingEnabled || false
                }
                financiers={monetaryDetails.taxFinanciers || []}
                lockedFinancingType={taxFinancingType}
                onSubmit={(values) =>
                  saveTranche(
                    "Tax Price",
                    values,
                    monetaryDetails.taxFinanciers || []
                  )
                }
                isSubmitting={isSavingTranche}
              />
            )}

            {taxTranches.length === 0 && !canAddMoreTaxTranches && (
              <div className="text-sm text-gray-400 py-2">
                No payment details yet.
              </div>
            )}
          </VStack>
        )}

        {/* ── Footer actions ─────────────────────────────────── */}
        <HStack className="justify-between gap-2 pt-2 border-t border-gray-100">
          <ButtonPrimary1
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </ButtonPrimary1>
        </HStack>
      </VStack>
    </div>
  );
};

export default AddExternalPaymentDetails;
